//@flow
import _ from 'lodash';
// app
import { JSableOmitPrivate } from 'jsable';
import type { Observable, ObservableArray, PureComputed } from 'rgs-knockout';
import ko from 'rgs-knockout';
// rgs-nfd-ui-kit
import {
	DEFAULT_DROPDOWN_pathToID,
	DEFAULT_DROPDOWN_pathToName,
	DEFAULT_DROPDOWN_PLACEHOLDER,
} from 'rgs-nfd-ui-kit/ko-components/dropdown';

export type DropdownListRequestParams = {
	search: ?string,
	take: number
}

export type TDropdownListOptionsParams<TItem, TItemID1> = {
	emptyPlaceholder?: string;
	pathToID?: string;
	pathToName?: string;
	isDisabled?: Observable<boolean>;
	isOpened?: Observable<boolean>;
	btnMoreIsShow?: Observable<boolean>;
	btnMoreContent?: Observable<string> | string;
	btnMoreClick?: (item: ?TItem) => void;
	items?: ObservableArray<TItem>;
	currentValue?: Observable<?TItem>;
	currentValueID?: Observable<?TItemID1>;
	beforeChange?: (oldValue: ?TItem, newValue: ?TItem) => void,
	onChange?: (?TItem) => void,
	onCancel?: () => void,
	shouldShowCancelBlock?: boolean,
	validation?: {
		validator: (...args: any) => any,
		message: string | (...args: any) => string
	}
}

type Options<TItem> = {
	beforeChange: (oldValue: ?TItem, newValue: ?TItem) => void,
	onChange: (?TItem) => void,
	onCancel: () => void,
	pathToID: string;
	pathToName: string;
	emptyPlaceholder: string;
};

export class DropdownListOptionsExtended<TItem: any, TItemID: any> extends JSableOmitPrivate {
	pathToName: string;
	pathToID: string;
	placeholder: { text: PureComputed<string> };
	items: ObservableArray<TItem>;
	currentValue: Observable<?TItem>;
	currentValueID: Observable<?TItemID>;
	isDisabled: Observable<boolean>;
	isOpened: Observable<boolean>;
	getCurrentName: () => ?string;

	_options: Options<TItem>;

	get pathToName (): string {
		return this._options.pathToName;
	}

	get pathToID (): string {
		return this._options.pathToID;
	}

	constructor (options: TDropdownListOptionsParams<TItem, TItemID>) {
		super();
		this._options = _.defaultsDeep(options, {
			beforeChange: () => undefined,
			onChange: () => undefined,
			onCancel: () => undefined,
			btnMoreClick: () => undefined,
			btnMoreContent: 'Показать ещё',
			btnMoreIsShow: ko.observable(false),
			pathToName: DEFAULT_DROPDOWN_pathToName,
			pathToID: DEFAULT_DROPDOWN_pathToID,
			emptyPlaceholder: DEFAULT_DROPDOWN_PLACEHOLDER,
			currentValue: ko.observable().extend({ toFromJS: true }),
			shouldShowCancelBlock: true,
		});

		this.btnMoreClick = this._options.btnMoreClick;
		this.btnMoreContent = this._options.btnMoreContent;
		this.btnMoreIsShow = this._options.btnMoreIsShow;

		this.currentValueID = options.currentValueID || ko.observable().extend({ toFromJS: true });
		this.isDisabled = options.isDisabled || ko.observable(false);
		this.isOpened = options.isOpened || ko.observable(false).extend({ toFromJS: true });

		this.items = options.items || ko.observableArray([]).extend({ toFromJS: true });
		this.currentValue = this._options.currentValue;
		this.validationValue = this.currentValue;
		if (options.validation) {
			this.currentValue.extend({
				validation: options.validation,
			});
		}

		this.activeValue = ko.pureComputed(() => {
			return this.currentValue() ? this.getItemID(this.currentValue()) : false;
		});

		this.placeholder = {
			text: ko.pureComputed(() => {
				return this.currentValue()
					? ko.unwrap(this.getItemName(this.currentValue()))
					: this._options.emptyPlaceholder;
			}),
		};

		this.currentValueID.subscribe((newItemID: TItemID) => {
			const items: TItem[] = this.items();
			if (this.pathToID && items.length) {
				const newCurrentValue: TItem | void = _.find(items, (item: TItem) => {
					return this.getItemID(item) === newItemID;
				});
				this.currentValue(newCurrentValue);
			}
		});

		this.items.subscribe((newValue: any) => {
			const currentID = this.currentValueID();
			if (this.pathToID && currentID) {
				const newCurrentValue: TItem | void = _.find(newValue, (item: any) => {
					return this.getItemID(item) === currentID;
				});
				this.currentValue(newCurrentValue);
			}
		});

		this.onCancel = {
			onClick: () => {
				if (!this.isDisabled()) {
					this.currentValueID(undefined);
					this._options.onCancel();
				}
			},
			visible: ko.pureComputed(() => {
				return this._options.shouldShowCancelBlock
					&& (!_.isNil(this.currentValueID()) && this.currentValueID() !== '');
			}),
		};

		this.getCurrentName = this._getCurrentName.bind(this);
	}

	getItemID = (item: ?TItem | ?Observable<TItem> | void): TItemID => {
		return ko.unwrap(_.get(ko.unwrap(item), this.pathToID));
	};

	getItemName = (item: ?TItem | ?Observable<TItem>): string => {
		return ko.unwrap(_.get(ko.unwrap(item), this.pathToName));
	};

	onChange = (item: TItem) => {
		const isRealUpdated = !this._isValuesEquality(this.currentValue(), item);

		isRealUpdated && this._options.beforeChange(this.currentValue(), item);

		this.currentValue(item);
		this.currentValueID(this.getItemID(item));
		this.isOpened(false);

		isRealUpdated && this._options.onChange(item);
	};

	_isValuesEquality (oldItem: ?TItem, newItem: ?TItem): boolean {
		return JSON.stringify(oldItem) === JSON.stringify(newItem);
	}

	getCurrentID = (): ?TItemID => {
		const currentValue = this.currentValue();
		return currentValue && this.getItemID(currentValue);
	};

	_getCurrentName = (): ?string => {
		return this.currentValue() && this.getItemName(this.currentValue());
	};
}
